// ============================================================================
// Feuille du noyau : ANALYSE — dérivées et primitives (refonte, lot 5.9, moteur v31).
// ============================================================================
// Le modèle donne la fonction (`kernel.start` : « f(x) = x × exp(x) »), ce qu'on cherche
// (`goal.kind` : `differentiate` → « f′(x) », `antiderivative` → « F(x) ») et la réponse
// (« f′(x) = (x + 1) × exp(x) »). L'élève écrit des lignes « tête = membres » :
//
//   f(x) = …     réécrire f (développer, factoriser, séparer)       1   analysis.rewrite@1
//   u(x) = …     nommer une partie de f                              1   analysis.name@1
//   u′(x) = …    dériver la partie nommée                            1   analysis.usual@1
//                (2 si elle demande elle-même une règle)
//   f′(x) = …    appliquer UNE règle à f, les calculs faits DANS chaque morceau :
//                  dérivées usuelles (terme à terme, g(ax + b))      2   analysis.usual@1
//                  produit (uv)′ = u′v + uv′                         2   analysis.product@1
//                  quotient (u′v − uv′) ÷ v²                         3   analysis.quotient@1
//                  inverse (1 ÷ v)′ = −v′ ÷ v²                       2   analysis.inverse@1
//                  composée (g(u))′ = u′ × g′(u)                     2   analysis.chain@1
//                puis remplacer u, v… par leurs expressions          1   analysis.substitute@1
//                puis développer, réduire, factoriser                1   analysis.rewrite@1
//   F(x) = …     une primitive : usuelle 2 (analysis.primitive@1), reconnue (u′eᵘ, u′ ÷ u,
//                u′uⁿ…) 3 (analysis.recognize@1) ; « + C » permis
//
// Dérivée seconde (lot 6.18) : la cible « f′′(x) » se calcule en deux étapes. La 1re
// dérive f (lignes « f′(x) = … », noms u, v… de parties de f) ; dès qu'une ligne « f′(x) = … »
// sans nom est écrite, la 2e étape dérive f′ (lignes « f′′(x) = … », noms de parties de f′,
// « f′(x) = … » réécrit f′). Une ligne « f′′(x) = … » juste avant f′ est gardée sans HORA.
//
// En un point (moteur v44, lot 6.42) :
//   TANGENTE (objectif `tangent`, `kernel.at` = a) : dériver f (lignes « f′(x) = … », qui ne
//   finissent pas la feuille), puis
//     f(a) = …, f′(a) = …     calculer en a (f′(a) : une fois f′(x) écrite)   1   analysis.point@1
//     y = f′(a)(x − a) + f(a) l'équation de la tangente (noms ou valeurs)     2   analysis.tangent@1
//     y = …                   remplacer f(a), f′(a) (1), développer, réduire (1)
//   PRIMITIVE AVEC CONDITION (`kernel.condition` = « F(0) = 2 ») :
//     F(x) = … + C            les primitives (la famille, jamais la réponse)
//     F(0) = … + C            calculer en a avec C                             1   analysis.point@1
//     C = …                   la condition donne C                             1   analysis.condition@1
//     F(x) = …                C remplacé (1) ; une ligne sans C doit vérifier la condition
//   L'énoncé peut ne donner que f′ (« f′(x) = 2x », cible « f(x) ») ; la constante e s'écrit « e ».
//
// Le juge compare des FONCTIONS : deux expressions sont égales si elles prennent les mêmes
// valeurs en des points de l'ensemble de définition (`kernel/calculus.ts`). Une ligne
// fausse est diagnostiquée (u′v′, le signe du quotient, u′ oublié, le coefficient…) sans
// jamais donner la bonne dérivée.
// ============================================================================

import { formatNumber, lex, normalize, type Chip } from './lexer';
import type { ScratchModel } from './model';
import { atomsText, parseLine, type Line } from './tree';
import {
  canon,
  fromNode,
  num,
  substitute,
  symbolsOf,
  termKey,
  termText,
  UnsupportedTerm,
  type Term,
} from './kernel/term';
import { evalFloat } from './kernel/normal';
import {
  ARC_TEXT_RE,
  CalculusUnsupported,
  MIN_POINTS,
  arcsActive,
  withArcDerivatives,
  SAMPLE_POINTS,
  closeTo,
  derive,
  groupsMatch,
  partsOf,
  splitConstant,
  stepTemplate,
  termSize,
  termsOf,
  valuesAt,
  vecEqual,
  vecOffset,
  vecRatio,
  type StepRule,
  type StepTemplate,
  type Vec,
} from './kernel/calculus';

export const ANALYSIS_STEP_IDS = {
  usual: 'analysis.usual@1',
  product: 'analysis.product@1',
  quotient: 'analysis.quotient@1',
  inverse: 'analysis.inverse@1',
  chain: 'analysis.chain@1',
  name: 'analysis.name@1',
  substitute: 'analysis.substitute@1',
  rewrite: 'analysis.rewrite@1',
  primitive: 'analysis.primitive@1',
  recognize: 'analysis.recognize@1',
  // moteur v44 : en un point (tangente, primitive avec condition)
  point: 'analysis.point@1',
  tangent: 'analysis.tangent@1',
  condition: 'analysis.condition@1',
} as const;
/** les pas « en un point » (moteur v44) */
export const ANALYSIS_POINT_STEP_IDS: string[] = [
  'analysis.point@1',
  'analysis.tangent@1',
  'analysis.condition@1',
];
type StepKind = keyof typeof ANALYSIS_STEP_IDS;

const STEP: Record<StepKind, { label: string; hora: number; why: string }> = {
  usual: {
    label: 'Dérivées usuelles',
    hora: 2,
    why: 'Terme à terme : (xⁿ)′ = nxⁿ⁻¹, (1 ÷ x)′ = −1 ÷ x², (√x)′ = 1 ÷ (2√x), (eˣ)′ = eˣ, (ln x)′ = 1 ÷ x ; et (g(ax + b))′ = a × g′(ax + b).',
  },
  product: {
    label: 'Dérivée d’un produit',
    hora: 2,
    why: '(uv)′ = u′v + uv′.',
  },
  quotient: {
    label: 'Dérivée d’un quotient',
    hora: 3,
    why: '(u ÷ v)′ = (u′v − uv′) ÷ v².',
  },
  inverse: {
    label: 'Dérivée de 1 ÷ v',
    hora: 2,
    why: '(1 ÷ v)′ = −v′ ÷ v².',
  },
  chain: {
    label: 'Dérivée d’une composée',
    hora: 2,
    why: '(g(u))′ = u′ × g′(u) : (eᵘ)′ = u′eᵘ, (ln u)′ = u′ ÷ u, (√u)′ = u′ ÷ (2√u), (uⁿ)′ = nu′uⁿ⁻¹, (cos u)′ = −u′ sin u.',
  },
  name: { label: 'Nommer', hora: 1, why: 'Une partie de f reçoit un nom.' },
  substitute: {
    label: 'Remplacer',
    hora: 1,
    why: 'Chaque nom est remplacé par son expression, les calculs faits dans chaque morceau.',
  },
  rewrite: { label: 'Réduire', hora: 1, why: 'La même fonction, écrite autrement.' },
  primitive: {
    label: 'Primitive usuelle',
    hora: 2,
    why: 'Terme à terme : xⁿ a pour primitive xⁿ⁺¹ ÷ (n + 1), 1 ÷ x a pour primitive ln x, eˣ a pour primitive eˣ… On vérifie en dérivant.',
  },
  recognize: {
    label: 'Reconnaître une forme',
    hora: 3,
    why: 'u′eᵘ a pour primitive eᵘ, u′ ÷ u a pour primitive ln u, u′uⁿ a pour primitive uⁿ⁺¹ ÷ (n + 1)… On vérifie en dérivant.',
  },
  point: {
    label: 'Calculer en un point',
    hora: 1,
    why: 'On remplace x par la valeur du point : f(a) avec l’expression de f, f′(a) avec celle de f′, F(a) avec celle de F.',
  },
  tangent: {
    label: 'Équation de la tangente',
    hora: 2,
    why: 'La tangente au point d’abscisse a : y = f′(a)(x − a) + f(a).',
  },
  condition: {
    label: 'Utiliser la condition',
    hora: 1,
    why: 'Les primitives diffèrent d’une constante C : la condition F(a) = … la fixe.',
  },
};

const CARD_D = 'Dérivation';
const CARD_P = 'Primitives';
const CARD_T = 'Tangente';

/** valeur de la constante d'intégration « C » aux points d'essai */
const C_VALUE = 0.6180339887;
/** une 2e valeur de C : une valeur en un point qui dépend de C se compare aux deux */
const C_ALT = 1.4142135623;

export interface KAnaVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  amove?: AMove;
  /** dérivée seconde : verdict de la 2e étape (f′ → f′′) */
  stage?: 2;
}

interface AMove {
  /** moteur v44 : `value` (« f(0) = … »), `c` (« C = … »), `y` (« y = … », la tangente) */
  kind: 'name' | 'deriv' | 'f' | 'target' | 'value' | 'c' | 'y';
  base: string;
  members: Term[];
  /** nom : sa définition ; dérivée d'un nom : son expression (noms remplacés) */
  value?: Term;
  final?: boolean;
  ruled?: boolean;
  /** `value` : la valeur (en C = C_VALUE et C = C_ALT) ; `c` : la constante trouvée */
  pair?: [number, number];
}

interface Head {
  base: string;
  primes: number;
}

interface Read {
  head: Head;
  members: Term[];
}

interface TrapRow {
  text: string;
  kind: 'target' | 'deriv' | 'f' | 'name';
  base: string;
  vec: Vec;
  line: Line;
  title: string;
  why: string;
}

export interface KAnaState {
  model: ScratchModel;
  /** la variable (« x ») */
  x: string;
  goal: 'differentiate' | 'antiderivative';
  /** la lettre de la fonction de l'énoncé (« f ») */
  fBase: string;
  /** la lettre de la cible (« f » pour f′, « F » pour une primitive) */
  tBase: string;
  points: number[];
  f: Term;
  fVec: Vec;
  /** les écritures de f (départ, puis réécritures validées) */
  fForms: Term[];
  /** derivative : f′ ; primitive : f (la dérivée d'une ligne F doit la valoir) */
  want: Vec;
  answer: Term;
  answerSize: number;
  names: Map<string, { def: Term; vec: Vec }>;
  derivs: Map<string, { expr: Term; vec: Vec }>;
  /** membres derniers des lignes cible validées (le dernier : la forme actuelle) */
  targets: Term[];
  concluded: boolean;
  traps: TrapRow[];
  givenTexts: Set<string>;
  path: string[];
  known: string[][];
  lines: { text: string }[];
  /**
   * dérivée seconde, 2e étape : la lettre de f ; ses têtes à primes perdent un cran
   * (« f′′(x) » est la cible, « f′(x) » la fonction qu'on dérive, nommée `fBase` = « f′ »)
   */
  shiftBase?: string;
  /** dérivée seconde : la 2e étape (f′ → f′′) ; l'état lui-même est la 1re (f → f′) */
  second?: KAnaState;
  /** valeurs des constantes aux points d'essai : C ; moteur v44 (en un point) : e */
  env: Record<string, number>;
  /** moteur v44 : la tangente en un point, ou la condition d'une primitive */
  point?: KPoint;
  /** moteur v60 : la feuille écrit arctan, arcsin ou arccos — dérivés et évalués en radians */
  arcs?: boolean;
}

/** moteur v60 : la feuille écrit-elle arctan, arcsin ou arccos ? */
function modelUsesArcs(model: ScratchModel): boolean {
  const k = model.kernel;
  if (!k) return false;
  return [
    k.start,
    k.answer,
    k.condition ?? '',
    ...(k.path ?? []),
    ...(model.givens ?? []),
    ...(model.traps ?? []).map((t) => t.line),
    ...(model.choices ?? []).map((c) => c.line ?? ''),
  ].some((t) => ARC_TEXT_RE.test(t));
}
/** moteur v60 : `f` dans le monde « arctan en radians » quand la feuille en écrit */
function inArcs<T>(ks: { arcs?: boolean }, f: () => T): T {
  return ks.arcs && !arcsActive() ? withArcDerivatives(f) : f();
}

/** moteur v44 : le point de la feuille (tangente, condition d'une primitive) */
interface KPoint {
  kind: 'tangent' | 'condition';
  /** l'abscisse telle qu'elle s'écrit dans les têtes (« 0 », « e ») et sa valeur */
  at: string;
  a: number;
  /** tangente : f(a), f′(a), la tangente aux points d'essai */
  fa: number;
  fpa: number;
  tangent: Vec;
  /** tangente : taille de la réponse (« y = x + 1 ») */
  answerSize: number;
  /** condition : « F(0) = 2 », sa tête, la valeur imposée (écrite, puis nombre) */
  condText: string;
  condHead: string;
  valueText: string;
  value: number;
  /** têtes calculées (« f(0) », « F(0) ») → leur valeur */
  values: Map<string, [number, number]>;
  /** la constante C trouvée */
  c: number | null;
  /** les dernières écritures des lignes « y = … » validées */
  ys: Term[];
  concluded: boolean;
}

/* ------------------------------ lecture ------------------------------ */

const SUP_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';
const isSupRun = (a: string): boolean =>
  a.length > 0 && [...a].every((c) => SUP_DIGITS.includes(c));

/** atomes d'une ligne : les exposants d'une suite (« ¹ » « ⁰ ») réunis en un bout (« ¹⁰ ») */
export function kanaLineAtoms(text: string): string[] {
  const out: string[] = [];
  for (const a of lex(text)) {
    const last = out[out.length - 1];
    if (isSupRun(a) && last !== undefined && isSupRun(last)) {
      out[out.length - 1] = last + a;
      continue;
    }
    out.push(a);
  }
  return out;
}

/** « x³ » → x ^ 3 pour le parseur (« ² » seul reste un carré) ; exporté pour le lot 6.20 */
export function caretAtoms(atoms: string[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < atoms.length; i++) {
    let j = i;
    let d = '';
    while (j < atoms.length && isSupRun(atoms[j]!)) {
      d += [...atoms[j]!].map((c) => String(SUP_DIGITS.indexOf(c))).join('');
      j++;
    }
    if (j > i && !(j - i === 1 && atoms[i] === '²')) {
      out.push('^', d);
      i = j - 1;
      continue;
    }
    out.push(atoms[i]!);
  }
  return out;
}

/** texte d'une ligne (relu à l'identique par `kanaLineAtoms`) */
export function kanaText(atoms: string[]): string {
  return atomsText(atoms)
    .replace(/ ([⁰¹²³⁴⁵⁶⁷⁸⁹]+)/g, '$1')
    .replace(/(\d) √/g, '$1√')
    .replace(/\//g, '÷')
    .replace(/\s*÷\s*/g, ' ÷ ');
}

const lineOf = (atoms: string[]): Line => ({
  dis: [{ sides: [{ k: 's', n: kanaText(atoms) }] }],
});
/** Ligne de feuille (texte) pour l'app. */
export function kanaLine(text: string): Line {
  return lineOf(kanaLineAtoms(text));
}

class ReadError extends Error {}

const NAME_RE = /^([A-Za-zα-ω][₀-₉]*)(′*)\(([^()]+)\)$/;

/** un nom de fonction « u(x) », « f′(x) » (en x) ; null sinon */
function nameOf(s: string, ks: Pick<KAnaState, 'x' | 'fBase' | 'shiftBase'>): Head | null {
  const m = NAME_RE.exec(s);
  if (!m || m[3] !== ks.x) return null;
  const h = { base: m[1]!, primes: m[2]!.length };
  // 2e étape d'une dérivée seconde : « f′(x) » est la fonction (« f′ »), « f′′(x) » sa dérivée
  if (ks.shiftBase && h.base === ks.shiftBase && h.primes >= 1)
    return { base: ks.fBase, primes: h.primes - 1 };
  return h;
}

/** le nombre de primes de la tête « f…(x) » d'une ligne (−1 : une autre tête) */
function headPrimes(text: string, fBase: string, x: string): number {
  const m = NAME_RE.exec(kanaLineAtoms(text)[0] ?? '');
  return m && m[1] === fBase && m[3] === x ? m[2]!.length : -1;
}

function readAtoms(ks: KAnaState, atoms: string[]): Read {
  const line = parseLine(normalize(caretAtoms(atoms)), { chains: true });
  if (line.dis.length !== 1) throw new ReadError('Une seule égalité par ligne.');
  const eq = line.dis[0]!;
  if (eq.cmp || eq.chain || eq.rels || eq.mod !== undefined || eq.sides.length < 2)
    throw new ReadError(
      `Écris une égalité « ${ks.tBase}${ks.goal === 'differentiate' ? '′' : ''}(${ks.x}) = … ».`,
    );
  const h = eq.sides[0]!;
  const head = h.k === 's' ? nameOf(h.n!, ks) : null;
  if (!head)
    throw new ReadError(
      `Commence la ligne par le nom de la fonction : « ${ks.tBase}${ks.goal === 'differentiate' ? '′' : ''}(${ks.x}) = … », « u(${ks.x}) = … ».`,
    );
  let members: Term[];
  try {
    // tels qu'écrits (pas de forme canonique : « 3 × 2 » n'est pas encore « 6 »)
    members = eq.sides.slice(1).map((s) => fromNode(s));
  } catch (e) {
    if (e instanceof UnsupportedTerm) throw new ReadError('Ce calcul ne se lit pas.');
    throw e;
  }
  return { head, members };
}

/* ------------------------------ valeurs ------------------------------ */

function namesIn(ks: KAnaState, t: Term): Head[] {
  const out: Head[] = [];
  symbolsOf(t).forEach((s) => {
    const h = nameOf(s, ks);
    if (h) out.push(h);
  });
  return out;
}

const nameKey = (h: Head): string => `${h.base}${'′'.repeat(h.primes)}(x)`;

/** le terme, chaque nom remplacé par son expression ; null si un nom n'est pas défini */
function resolve(ks: KAnaState, t: Term): { t: Term } | { missing: Head } {
  const map: Record<string, Term> = {};
  for (const s of symbolsOf(t)) {
    const h = nameOf(s, ks);
    if (!h) continue;
    const r = resolveName(ks, h);
    if (!r) return { missing: h };
    map[s] = r;
  }
  return { t: Object.keys(map).length ? canon(substitute(t, map)) : t };
}

function resolveName(ks: KAnaState, h: Head): Term | null {
  if (h.base === ks.fBase) {
    if (h.primes === 0) return ks.f;
    if (h.primes === 1) return safeDerive(ks.f, ks.x);
    return null;
  }
  const n = ks.names.get(h.base);
  if (!n) return null;
  if (h.primes === 0) return n.def;
  if (h.primes === 1) return ks.derivs.get(h.base)?.expr ?? safeDerive(n.def, ks.x);
  return null;
}

function safeDerive(t: Term, x: string): Term | null {
  try {
    return derive(t, x);
  } catch {
    return null;
  }
}

const vecOf = (ks: KAnaState, t: Term): Vec => valuesAt(t, ks.x, ks.points, ks.env);

function vecR(ks: KAnaState, t: Term): Vec | null {
  const r = resolve(ks, t);
  return 'missing' in r ? null : vecOf(ks, r.t);
}

const same = (a: Vec, b: Vec): boolean => vecEqual(a, b);

/* ------------------------------ état ------------------------------ */

function inDomain(domain: Line | null, x: string, p: number): boolean {
  if (!domain) return true;
  const eq = domain.dis[0]!;
  const vals = eq.sides.map((s) => {
    try {
      return evalFloat(canon(fromNode(s)), { [x]: p });
    } catch {
      return NaN;
    }
  });
  const rels = eq.chain ?? (eq.cmp ? [eq.cmp] : []);
  return rels.every((r, i) => {
    const a = vals[i]!;
    const b = vals[i + 1]!;
    if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
    switch (r) {
      case '<':
        return a < b;
      case '>':
        return a > b;
      case '≤':
        return a <= b;
      case '≥':
        return a >= b;
      default:
        return true;
    }
  });
}

export function createKAnaState(model: ScratchModel): KAnaState {
  if (modelUsesArcs(model) && !arcsActive()) {
    const ks = withArcDerivatives(() => createKAnaState(model));
    ks.arcs = true;
    if (ks.second) ks.second.arcs = true;
    return ks;
  }
  const spec = model.kernel!;
  const goal = spec.goal.kind === 'antiderivative' ? 'antiderivative' : 'differentiate';
  // moteur v44 : la tangente en un point, une primitive avec une condition
  const tangent = spec.goal.kind === 'tangent';
  const pointGoal = tangent || !!spec.condition;
  const startAtoms = kanaLineAtoms(spec.start);
  const sline = parseLine(normalize(caretAtoms(startAtoms)), { chains: true });
  const s0 = sline.dis[0]!;
  if (sline.dis.length !== 1 || s0.sides.length !== 2 || s0.sides[0]!.k !== 's')
    throw new Error('Départ attendu : « f(x) = … ».');
  const m = NAME_RE.exec(s0.sides[0]!.n!);
  // une primitive peut partir de f′ (« f′(x) = 2x », on cherche f) : avec une condition
  // (moteur v44), ou sans (moteur v63 : « v′(x) = sin(x) », on cherche v)
  const fromPrime = goal === 'antiderivative' && m?.[2] === '′';
  if (!m || (m[2] && !fromPrime)) throw new Error('Départ attendu : « f(x) = … ».');
  const x = m[3]!;
  if (!/^[a-zα-ω]$/.test(x)) throw new Error('La variable est une lettre : « f(x) ».');
  const fBase = fromPrime ? `${m[1]!}′` : m[1]!;
  // la tangente : la 1re étape dérive f
  const targetText = tangent ? `${fBase}′(${x})` : spec.goal.target;
  const tm = NAME_RE.exec(targetText);
  if (!tm || tm[3] !== x) throw new Error(`Cible attendue : « ${fBase}′(${x}) » ou « F(${x}) ».`);
  if (fromPrime && tm[1] !== m[1])
    throw new Error(`Cible attendue : « ${m[1]!}(${x}) » (l’énoncé donne ${fBase}).`);
  const tBase = tm[1]!;
  // dérivée seconde (lot 6.18) : « f′′(x) »
  const order2 = goal === 'differentiate' && tm[2] === '′′';
  if (goal === 'differentiate' && (tBase !== fBase || (tm[2] !== '′' && !order2)))
    throw new Error(`Cible attendue : « ${fBase}′(${x}) » ou « ${fBase}′′(${x}) ».`);
  if (goal === 'antiderivative' && (tBase === fBase || tm[2]))
    throw new Error(`Cible attendue : « F(${x}) ».`);
  const f = canon(fromNode(s0.sides[1]!));
  let fprime: Term;
  let fsecond: Term | null = null;
  try {
    fprime = derive(f, x);
    if (order2) fsecond = derive(fprime, x);
  } catch (e) {
    throw new Error(e instanceof CalculusUnsupported ? e.message : String(e));
  }
  const domain = spec.domain
    ? parseLine(normalize(caretAtoms(kanaLineAtoms(spec.domain))), { chains: true })
    : null;
  const lastMember = (text: string): Term => {
    const r = parseLine(normalize(caretAtoms(kanaLineAtoms(text))), { chains: true });
    const sides = r.dis[0]!.sides;
    return fromNode(sides[sides.length - 1]!);
  };
  const answer = tangent ? fprime : lastMember(spec.answer);
  const env: Record<string, number> = pointGoal ? { C: C_VALUE, e: Math.E } : { C: C_VALUE };
  const finite = (t: Term, p: number): boolean => {
    try {
      return Number.isFinite(evalFloat(t, { ...env, [x]: p }));
    } catch {
      return false;
    }
  };
  const dAnswer = goal === 'antiderivative' ? safeDerive(answer, x) : null;
  const points = SAMPLE_POINTS.filter(
    (p) =>
      inDomain(domain, x, p) &&
      finite(f, p) &&
      finite(fprime, p) &&
      (!fsecond || finite(fsecond, p)) &&
      finite(answer, p) &&
      (goal === 'differentiate' || (!!dAnswer && finite(dAnswer, p))),
  );
  if (points.length < MIN_POINTS)
    throw new Error('Trop peu de points où la fonction est définie : précise l’intervalle.');
  const path = spec.path ?? [];
  // dérivée seconde : le corrigé se coupe à sa 1re ligne « f′′(x) = … »
  const cut = order2 ? path.findIndex((t) => headPrimes(t, fBase, x) === 2) : -1;
  const path1 = cut < 0 ? path : path.slice(0, cut);
  const path2 = cut < 0 ? [] : path.slice(cut);
  const point = pointGoal
    ? createPoint(spec, { x, f, fprime, fBase, tBase, env, domain, lastMember })
    : undefined;
  const shared = {
    model,
    x,
    goal,
    points,
    givenTexts: new Set([kanaText(startAtoms), ...(point?.condText ? [point.condText] : [])]),
    known: [...path, spec.answer].map(kanaLineAtoms),
    env,
  } as const;
  const ks = stageState(shared, {
    fBase,
    tBase,
    // l'énoncé donne f′ : « f′(x) » est la fonction (comme la 2e étape d'une dérivée seconde)
    ...(fromPrime ? { shiftBase: m[1]! } : {}),
    f,
    want: goal === 'differentiate' ? fprime : null,
    answer: order2 ? fprime : answer,
    // 1re étape d'une dérivée seconde, ou d'une tangente : toute écriture de f′ sans nom la
    // termine
    answerSize:
      order2 || tangent
        ? Infinity
        : termSize(goal === 'antiderivative' ? dropConstants(answer, x) : answer),
    path: path1,
  });
  if (point) {
    ks.point = point;
    point.tangent = tangent ? ks.points.map((p) => point.fpa * (p - point.a) + point.fa) : [];
  }
  if (order2)
    ks.second = stageState(shared, {
      fBase: `${fBase}′`,
      tBase: `${fBase}′`,
      shiftBase: fBase,
      f: fprime,
      want: fsecond,
      answer,
      answerSize: termSize(answer),
      // les réécritures de f′ du corrigé (« f′(x) = (x + 1) × exp(x) »), puis la 2e étape
      path: [...path1.filter((t) => headPrimes(t, fBase, x) === 1), ...path2],
    });
  for (const t of model.traps ?? []) {
    const stage = ks.second && headPrimes(t.line, fBase, x) === 2 ? ks.second : ks;
    try {
      const r = readAtoms(stage, kanaLineAtoms(t.line));
      const last = r.members[r.members.length - 1]!;
      const v = vecR(stage, last);
      if (!v) continue;
      stage.traps.push({
        text: kanaText(kanaLineAtoms(t.line)),
        kind: kindOf(stage, r.head),
        base: r.head.base,
        vec: v,
        line: kanaLine(t.line),
        title: t.title ?? 'Piège',
        why: t.why ?? '',
      });
    } catch {
      /* piège illisible : la porte le signale */
    }
  }
  return ks;
}

/** un état d'étape : la fonction à dériver (ou à intégrer), ce qu'on cherche, son corrigé */
function stageState(
  shared: Pick<KAnaState, 'model' | 'x' | 'goal' | 'points' | 'givenTexts' | 'known' | 'env'>,
  o: {
    fBase: string;
    tBase: string;
    shiftBase?: string;
    f: Term;
    /** dérivée attendue ; null : une primitive (sa dérivée doit valoir f) */
    want: Term | null;
    answer: Term;
    answerSize: number;
    path: string[];
  },
): KAnaState {
  const ks: KAnaState = {
    ...shared,
    fBase: o.fBase,
    tBase: o.tBase,
    ...(o.shiftBase ? { shiftBase: o.shiftBase } : {}),
    f: o.f,
    fVec: [],
    fForms: [o.f],
    want: [],
    answer: o.answer,
    answerSize: o.answerSize,
    names: new Map(),
    derivs: new Map(),
    targets: [],
    concluded: false,
    traps: [],
    path: o.path,
    lines: [],
  };
  ks.fVec = vecOf(ks, o.f);
  ks.want = o.want ? vecOf(ks, o.want) : ks.fVec;
  return ks;
}

/** moteur v44 : le point de la feuille (tangente en a, condition F(a) = …) */
function createPoint(
  spec: NonNullable<ScratchModel['kernel']>,
  o: {
    x: string;
    f: Term;
    fprime: Term;
    fBase: string;
    tBase: string;
    env: Record<string, number>;
    domain: Line | null;
    lastMember: (text: string) => Term;
  },
): KPoint {
  const evalText = (text: string): number => {
    const v = evalFloat(canon(o.lastMember(`a = ${text}`)), o.env);
    if (!Number.isFinite(v)) throw new Error(`« ${text} » ne se calcule pas.`);
    return v;
  };
  const base: KPoint = {
    kind: 'tangent',
    at: '',
    a: 0,
    fa: 0,
    fpa: 0,
    tangent: [],
    answerSize: 0,
    condText: '',
    condHead: '',
    valueText: '',
    value: 0,
    values: new Map(),
    c: null,
    ys: [],
    concluded: false,
  };
  if (spec.goal.kind === 'tangent') {
    const at = spec.at!;
    const a = evalText(at);
    if (!inDomain(o.domain, o.x, a)) throw new Error(`Le point ${at} n’est pas dans l’intervalle.`);
    const at2 = (t: Term): number => evalFloat(t, { ...o.env, [o.x]: a });
    const fa = at2(o.f);
    const fpa = at2(o.fprime);
    if (!Number.isFinite(fa) || !Number.isFinite(fpa))
      throw new Error(`${o.fBase} n’est pas dérivable en ${at}.`);
    const y = parseLine(normalize(caretAtoms(kanaLineAtoms(spec.answer))), { chains: true });
    const head = y.dis[0]?.sides[0];
    if (y.dis.length !== 1 || !head || head.k !== 's' || head.n !== 'y')
      throw new Error('Réponse attendue : « y = … ».');
    return {
      ...base,
      at,
      a,
      fa,
      fpa,
      answerSize: termSize(o.lastMember(spec.answer)),
    };
  }
  // la condition « F(0) = 2 » : la tête est la cible, en un point
  const atoms = kanaLineAtoms(spec.condition!);
  const line = parseLine(normalize(caretAtoms(atoms)), { chains: true });
  const eq = line.dis[0]!;
  const h = eq.sides[0]!;
  const hm = h.k === 's' ? NAME_RE.exec(h.n!) : null;
  if (line.dis.length !== 1 || eq.sides.length !== 2 || eq.cmp || !hm || hm[1] !== o.tBase || hm[2])
    throw new Error(`Condition attendue : « ${o.tBase}(a) = … ».`);
  const at = hm[3]!;
  const eqAt = atoms.indexOf('=');
  return {
    ...base,
    kind: 'condition',
    at,
    a: evalText(at),
    condText: kanaText(atoms),
    condHead: h.n!,
    valueText: kanaText(atoms.slice(eqAt + 1)),
    value: evalFloat(canon(fromNode(eq.sides[1]!)), o.env),
  };
}

/** les termes sans x d'une somme retirés (la constante d'une primitive) */
function dropConstants(t: Term, x: string): Term {
  const ts = termList(t).filter((c) => symbolsOf(c).has(x));
  if (!ts.length) return t;
  return ts.length === 1 ? ts[0]! : { k: 'add', c: ts };
}

/** les termes d'une somme TELLE QU'ÉCRITE (sans regrouper les termes semblables) */
export function termList(t: Term): Term[] {
  if (t.k === 'add') return t.c.flatMap(termList);
  if (t.k === 'num' && t.v.n === 0n) return [];
  return [t];
}

/** même écriture, au calcul près : rien de nouveau si rien n'a été calculé ni réécrit */
function sameWriting(a: Term, b: Term): boolean {
  return termKey(canon(a)) === termKey(canon(b)) && termSize(b) >= termSize(a);
}

function kindOf(ks: KAnaState, h: Head): TrapRow['kind'] {
  if (h.base === ks.tBase && (ks.goal === 'differentiate' ? h.primes === 1 : h.primes === 0))
    return 'target';
  if (h.base === ks.fBase && h.primes === 0) return 'f';
  return h.primes === 0 ? 'name' : 'deriv';
}

/* ------------------------------ gabarits ------------------------------ */

const isZeroVec = (v: Vec): boolean => v.every((x) => closeTo(x, 0));

/** les termes d'une expression, en valeurs (noms remplacés) ; null si un nom manque */
function termVecs(ks: KAnaState, t: Term): Vec[] | null {
  const out: Vec[] = [];
  for (const c of termList(t)) {
    const v = vecR(ks, c);
    if (!v) return null;
    out.push(v);
  }
  return out;
}

/** numérateur et dénominateur d'une écriture « N ÷ D » (un seul terme), sinon null */
function numDen(t: Term): { n: Term; d: Term } | null {
  const c = canon(t);
  if (c.k === 'div') return { n: c.a, d: c.b };
  if (c.k === 'mul') {
    const divs = c.c.filter((f) => f.k === 'div');
    if (divs.length !== 1) return null;
    const dv = divs[0] as Extract<Term, { k: 'div' }>;
    const rest = c.c.filter((f) => f !== dv);
    return { n: canon({ k: 'mul', c: [...rest, dv.a] }), d: dv.b };
  }
  return null;
}

const scale = (v: Vec, k: number): Vec => v.map((a) => a * k);
const divide = (a: Vec, b: Vec): Vec => a.map((v, i) => v / b[i]!);

/** l'écriture E applique-t-elle le gabarit (les calculs faits dans chaque morceau) ? */
function matchesTemplate(ks: KAnaState, E: Term, tpl: StepTemplate): boolean {
  const pieces = tpl.pieces.map((p) => vecOf(ks, p));
  if (tpl.den) {
    const den = vecOf(ks, tpl.den);
    const nd = numDen(E);
    if (nd) {
      const dv = vecR(ks, nd.d);
      const r = dv ? vecRatio(dv, den) : null;
      if (r !== null && !closeTo(r, 0)) {
        const nts = termVecs(ks, nd.n);
        if (
          nts &&
          groupsMatch(
            nts.map((v) => scale(v, 1 / r)),
            pieces,
          )
        )
          return true;
      }
    }
    const ets = termVecs(ks, E);
    return (
      !!ets &&
      groupsMatch(
        ets,
        pieces.map((p) => divide(p, den)),
      )
    );
  }
  const ets = termVecs(ks, E);
  return !!ets && groupsMatch(ets, pieces);
}

function knownDerivative(ks: KAnaState) {
  return (part: Term): Term | null => {
    const pv = vecOf(ks, part);
    for (const [base, n] of ks.names) {
      const d = ks.derivs.get(base);
      if (d && same(n.vec, pv)) return d.expr;
    }
    return null;
  };
}

const ruleKind = (r: StepRule): StepKind =>
  r === 'usual'
    ? 'usual'
    : r === 'product'
      ? 'product'
      : r === 'quotient'
        ? 'quotient'
        : r === 'inverse'
          ? 'inverse'
          : 'chain';

interface TplMatch {
  rules: StepKind[];
  nested: Term[];
  affine: boolean;
}

/** la ligne E dérive-t-elle une des écritures de `target` (f, ou la partie nommée) ? */
function templateMatch(ks: KAnaState, forms: Term[], E: Term): TplMatch | { fail: StepRule[] } {
  let nestedHit: TplMatch | null = null;
  let firstRules: StepRule[] = [];
  for (const form of forms) {
    const tpl = stepTemplate(form, ks.x, knownDerivative(ks));
    if (!tpl) continue;
    if (!firstRules.length) firstRules = tpl.rules;
    if (!matchesTemplate(ks, E, tpl)) continue;
    const m: TplMatch = {
      rules: [...new Set(tpl.rules.map(ruleKind))],
      nested: tpl.nested,
      affine: hasAffineInner(form, ks.x),
    };
    if (!tpl.nested.length) return m;
    nestedHit ??= m;
  }
  return nestedHit ?? { fail: firstRules };
}

/** f contient-elle une fonction usuelle d'une expression affine non réduite à x ? */
function hasAffineInner(t: Term, x: string): boolean {
  const affine = (a: Term): boolean => {
    if (a.k === 'sym') return false;
    if (!symbolsOf(a).has(x)) return false;
    try {
      return !symbolsOf(derive(a, x)).has(x);
    } catch {
      return false;
    }
  };
  switch (t.k) {
    case 'fn':
      return affine(t.a) || hasAffineInner(t.a, x);
    case 'pow':
      return (!symbolsOf(t.b).has(x) && affine(t.a)) || hasAffineInner(t.a, x);
    case 'div':
      return affine(t.b) || hasAffineInner(t.a, x) || hasAffineInner(t.b, x);
    case 'add':
    case 'mul':
      return t.c.some((c) => hasAffineInner(c, x));
    default:
      return false;
  }
}

/* ------------------------------ diagnostics ------------------------------ */

interface Diag {
  title: string;
  message: string;
}

/** les erreurs classiques sur un terme de f, en valeurs de la dérivée ENTIÈRE fausse */
function classicMistakes(ks: KAnaState): { vec: Vec; d: Diag }[] {
  const out: { vec: Vec; d: Diag }[] = [];
  const x = ks.x;
  const want = ks.want;
  for (const t of termsOf(ks.f)) {
    let dt: Term;
    try {
      dt = derive(t, x);
    } catch {
      continue;
    }
    const base = want.map((v, i) => v - vecOf(ks, dt)[i]!);
    const alt = (w: Term, d: Diag) => {
      try {
        const wv = vecOf(ks, canon(w));
        out.push({ vec: base.map((v, i) => v + wv[i]!), d });
      } catch {
        /* écriture impossible */
      }
    };
    // un terme oublié
    if (termsOf(ks.f).length > 1 && !isZeroVec(vecOf(ks, dt)))
      out.push({
        vec: base,
        d: {
          title: 'Il manque un morceau',
          message: 'Dérive chaque terme de la somme : il en manque un.',
        },
      });
    const { c, core } = splitConstant(t, x);
    const k = (...fs: Term[]): Term => ({ k: 'mul', c: [...c, ...fs] });
    const neg1 = num(-1);
    const d = (u: Term): Term | null => safeDerive(u, x);
    if (core.length === 2) {
      const [u, v] = core as [Term, Term];
      const du = d(u);
      const dv = d(v);
      if (du && dv) {
        alt(k(du, dv), {
          title: '(uv)′ n’est pas u′v′',
          message: 'La dérivée d’un produit n’est pas le produit des dérivées : (uv)′ = u′v + uv′.',
        });
        alt(k(du, v), {
          title: 'Il manque un terme',
          message: '(uv)′ = u′v + uv′ : deux termes.',
        });
        alt(k(u, dv), {
          title: 'Il manque un terme',
          message: '(uv)′ = u′v + uv′ : deux termes.',
        });
      }
    }
    const g = core.length === 1 ? core[0]! : null;
    if (g && g.k === 'div') {
      const u = g.a;
      const v = g.b;
      const du = d(u);
      const dv = d(v);
      const v2: Term = { k: 'pow', a: v, b: num(2) };
      if (du && dv && symbolsOf(u).has(x)) {
        alt(k({ k: 'div', a: { k: 'add', c: [k(du, v), k(u, dv)] }, b: v2 }), {
          title: 'Le signe',
          message: '(u ÷ v)′ = (u′v − uv′) ÷ v² : un MOINS au numérateur.',
        });
        alt(
          k({
            k: 'div',
            a: {
              k: 'add',
              c: [
                { k: 'mul', c: [u, dv] },
                { k: 'mul', c: [neg1, du, v] },
              ],
            },
            b: v2,
          }),
          {
            title: 'L’ordre',
            message: '(u ÷ v)′ = (u′v − uv′) ÷ v² : u′v d’abord.',
          },
        );
        alt(
          k({
            k: 'div',
            a: {
              k: 'add',
              c: [
                { k: 'mul', c: [du, v] },
                { k: 'mul', c: [neg1, u, dv] },
              ],
            },
            b: v,
          }),
          {
            title: 'Le dénominateur',
            message: '(u ÷ v)′ = (u′v − uv′) ÷ v² : le dénominateur est v², au carré.',
          },
        );
        alt(k({ k: 'div', a: du, b: dv }), {
          title: '(u ÷ v)′ n’est pas u′ ÷ v′',
          message:
            'La dérivée d’un quotient n’est pas le quotient des dérivées : (u ÷ v)′ = (u′v − uv′) ÷ v².',
        });
      } else if (dv && !symbolsOf(u).has(x)) {
        alt(k({ k: 'div', a: { k: 'mul', c: [u, dv] }, b: v2 }), {
          title: 'Le signe',
          message: '(1 ÷ v)′ = −v′ ÷ v² : un signe moins.',
        });
        alt(k({ k: 'div', a: { k: 'mul', c: [neg1, u, dv] }, b: v }), {
          title: 'Le dénominateur',
          message: '(1 ÷ v)′ = −v′ ÷ v² : le dénominateur est v², au carré.',
        });
      }
    }
    if (g && (g.k === 'fn' || (g.k === 'pow' && !symbolsOf(g.b).has(x)))) {
      const u = g.k === 'fn' ? g.a : g.a;
      const du = d(u);
      const full = d(g);
      if (du && full && symbolsOf(u).has(x) && !isZeroVec(vecOf(ks, du))) {
        const outer = canon({ k: 'div', a: full, b: du });
        if (!(u.k === 'sym')) {
          alt(k(outer), {
            title: 'Multiplie par u′',
            message: '(g(u))′ = u′ × g′(u) : la dérivée de l’intérieur manque.',
          });
        }
        if (g.k === 'fn' && g.f === 'sqrt')
          alt(k({ k: 'div', a: du, b: g }), {
            title: 'Le 2',
            message: '(√u)′ = u′ ÷ (2√u).',
          });
        if (g.k === 'pow')
          alt(k(g.b, du, g), {
            title: 'L’exposant',
            message: '(uⁿ)′ = n × u′ × uⁿ⁻¹ : l’exposant baisse de 1.',
          });
      }
    }
  }
  return out;
}

function wrongDerivative(ks: KAnaState, v: Vec): Diag {
  for (const m of classicMistakes(ks)) if (same(v, m.vec)) return m.d;
  if (same(v, ks.fVec) && !same(ks.fVec, ks.want))
    return { title: `C’est ${ks.fBase}`, message: `Tu as recopié ${ks.fBase} : dérive-la.` };
  const r = vecRatio(v, ks.want);
  if (r !== null && closeTo(r, -1))
    return {
      title: 'Le signe',
      message: 'Tout est juste au signe près : vérifie les signes moins.',
    };
  if (r !== null && !closeTo(r, 0) && !closeTo(r, 1))
    return {
      title: 'Le coefficient',
      message: 'La forme est la bonne, mais pas le coefficient devant : vérifie-le.',
    };
  const off = vecOffset(v, ks.want);
  if (off !== null && !closeTo(off, 0))
    return { title: 'La constante', message: 'La dérivée d’une constante est 0.' };
  return { title: 'Dérivée fausse', message: `Ce n’est pas la dérivée de ${ks.fBase}.` };
}

/** « F(x) telle que F′ = f » ; l'énoncé donne f′ (moteur v44) : « f(x) connaissant f′(x) » */
function seekText(ks: KAnaState): string {
  return ks.fBase === `${ks.tBase}′`
    ? `${ks.tBase}(${ks.x}) connaissant ${ks.fBase}(${ks.x})`
    : `${ks.tBase}(${ks.x}) telle que ${ks.tBase}′ = ${ks.fBase}`;
}

function wrongPrimitive(ks: KAnaState, dE: Vec, E: Vec): Diag {
  const f = ks.fBase;
  // « F telle que F′ = f » (sans « (x) » : l'écriture d'avant le moteur v44)
  const seek =
    ks.fBase === `${ks.tBase}′` ? seekText(ks) : `${ks.tBase} telle que ${ks.tBase}′ = ${ks.fBase}`;
  if (same(E, ks.fVec))
    return {
      title: `C’est ${f}`,
      message: `Tu as recopié ${f} : on cherche ${seek}.`,
    };
  const fd = (() => {
    const d = safeDerive(ks.f, ks.x);
    return d ? vecOf(ks, d) : null;
  })();
  if (fd && same(E, fd))
    return {
      title: 'Tu as dérivé',
      message: `C’est la dérivée de ${f} : on cherche une primitive, ${seek}.`,
    };
  const r = vecRatio(dE, ks.fVec);
  if (r !== null && closeTo(r, -1))
    return {
      title: 'Le signe',
      message: `En dérivant ta proposition, on trouve −${f} : change le signe.`,
    };
  if (r !== null && !closeTo(r, 0) && !closeTo(r, 1))
    return {
      title: 'Le coefficient',
      message: `En dérivant ta proposition, on trouve ${formatNumber(r)} × ${f} : ajuste le coefficient.`,
    };
  return {
    title: 'Pas une primitive',
    message: `Dérive ta proposition : tu ne retrouves pas ${f}.`,
  };
}

const RULE_HINT: Record<StepRule, string> = {
  usual: 'Dérive terme à terme avec les dérivées usuelles.',
  product: 'Écris d’abord (uv)′ = u′v + uv′ : nomme u et v.',
  quotient: 'Écris d’abord (u ÷ v)′ = (u′v − uv′) ÷ v² : nomme u et v.',
  inverse: 'Écris d’abord (1 ÷ v)′ = −v′ ÷ v².',
  chain_exp: 'Écris d’abord (eᵘ)′ = u′eᵘ : nomme u.',
  chain_ln: 'Écris d’abord (ln u)′ = u′ ÷ u : nomme u.',
  chain_sqrt: 'Écris d’abord (√u)′ = u′ ÷ (2√u) : nomme u.',
  chain_pow: 'Écris d’abord (uⁿ)′ = n × u′ × uⁿ⁻¹ : nomme u.',
  chain_trig: 'Écris d’abord (cos u)′ = −u′ sin u, (sin u)′ = u′ cos u : nomme u.',
  chain_arc: 'Écris d’abord (arctan u)′ = u′ ÷ (1 + u²) : nomme u.',
};

/* ------------------------------ juge ------------------------------ */

function strictPrefixOfKnown(ks: KAnaState, A: string[]): boolean {
  return ks.known.some((k) => k.length > A.length && A.every((a, i) => k[i] === a));
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

function judgeStage(ks: KAnaState, chips: Chip[], opts?: { verify?: boolean }): KAnaVerdict {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const prefix = strictPrefixOfKnown(ks, A);
  let read: Read;
  try {
    read = readAtoms(ks, A);
  } catch (e) {
    if (!opts?.verify) return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
    if (e instanceof ReadError)
      return {
        kind: 'syntax',
        atoms: A,
        line: lineOf(A),
        title: 'Une égalité',
        message: e.message,
      };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça ne se lit pas',
      message: 'Termine ta ligne : il manque un morceau.',
      badChip: chips.length - 1,
    };
  }
  const v = judgeRead(ks, A, read);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}

type Base = { atoms: string[]; line: Line };

function trapOr(ks: KAnaState, kind: TrapRow['kind'], v: Vec, base: Base, d: Diag): KAnaVerdict {
  const text = kanaText(base.atoms);
  const t = ks.traps.find((t) => t.text === text || (t.kind === kind && same(t.vec, v)));
  if (t)
    return {
      kind: 'trap',
      ...base,
      title: t.title,
      message: t.why,
      trap: { line: t.line, title: t.title, why: t.why },
    };
  return { kind: 'false', ...base, title: d.title, message: d.message };
}

function missingName(ks: KAnaState, h: Head, base: Base): KAnaVerdict {
  const n = nameKey(h).replace('(x)', `(${ks.x})`);
  if (h.primes > 1)
    return {
      kind: 'unjustified',
      ...base,
      title: 'Une dérivée à la fois',
      message: 'Ici, on ne dérive qu’une fois.',
    };
  return {
    kind: 'unjustified',
    ...base,
    title: `Nomme d’abord ${h.base}`,
    message:
      h.primes === 0
        ? `Écris d’abord ce que vaut ${n} : « ${n} = … ».`
        : `Écris d’abord ${h.base}(${ks.x}) = …, puis sa dérivée.`,
  };
}

function judgeRead(ks: KAnaState, A: string[], read: Read): KAnaVerdict {
  const base: Base = { atoms: A, line: lineOf(A) };
  if (ks.givenTexts.has(kanaText(A)))
    return {
      kind: 'useless',
      ...base,
      title: 'C’est l’énoncé',
      message: 'Cette ligne est dans l’énoncé : écris la suivante.',
    };
  const { head, members } = read;
  const kind = kindOf(ks, head);
  if (head.primes > 1) return missingName(ks, head, base);
  // F′(x) = … quand on cherche une primitive : la définition, rien de plus
  if (ks.goal === 'antiderivative' && head.base === ks.tBase && head.primes === 1)
    return {
      kind: 'useless',
      ...base,
      title: 'C’est la définition',
      message: `On cherche ${seekText(ks)} : écris « ${ks.tBase}(${ks.x}) = … ».`,
    };
  if (ks.goal === 'antiderivative' && head.base === ks.fBase && head.primes === 1)
    return {
      kind: 'useless',
      ...base,
      title: 'On cherche une primitive',
      message: `Ici, on ne dérive pas ${ks.fBase} : on cherche ${seekText(ks)}.`,
    };
  // les noms utilisés doivent être définis
  for (const m of members)
    for (const h of namesIn(ks, m)) {
      if (h.base === ks.tBase && ks.goal === 'antiderivative')
        return {
          kind: 'unjustified',
          ...base,
          title: 'Écris F',
          message: `Écris l’expression de ${ks.tBase}(${ks.x}), sans ${ks.tBase} dedans.`,
        };
      if (!resolveName(ks, h)) return missingName(ks, h, base);
    }
  if (kind === 'name') return judgeName(ks, base, head, members);
  if (kind === 'deriv' && !ks.names.has(head.base))
    return missingName(ks, { base: head.base, primes: 0 }, base);
  if (kind === 'deriv') return judgeDeriv(ks, base, head, members);
  if (kind === 'f') return judgeF(ks, base, members);
  return ks.goal === 'differentiate'
    ? judgeTargetD(ks, base, members)
    : judgeTargetP(ks, base, members);
}

/** u(x) = … : une partie de f reçoit un nom */
function judgeName(ks: KAnaState, base: Base, head: Head, members: Term[]): KAnaVerdict {
  const vecs = members.map((m) => vecR(ks, m)!);
  if (!vecs.every((v) => same(v, vecs[0]!)))
    return {
      kind: 'false',
      ...base,
      title: 'Pas la même fonction',
      message: 'Les membres de ta ligne ne sont pas égaux.',
    };
  const v = vecs[0]!;
  const old = ks.names.get(head.base);
  if (old) {
    if (same(old.vec, v))
      return {
        kind: 'useless',
        ...base,
        title: 'Rien de nouveau',
        message: `${head.base} est déjà nommé.`,
      };
    return {
      kind: 'false',
      ...base,
      title: `${head.base} est déjà pris`,
      message: `${head.base}(${ks.x}) a déjà une autre expression : choisis une autre lettre.`,
    };
  }
  const isPart = ks.fForms.some((form) => partsOf(form, ks.x).some((p) => same(vecOf(ks, p), v)));
  if (!isPart)
    return {
      kind: 'useless',
      ...base,
      title: `Pas une partie de ${ks.fBase}`,
      message: `${head.base} doit être une partie de ${ks.fBase} : un facteur, le numérateur, le dénominateur, ce qui est sous une fonction…`,
    };
  const def = (() => {
    const r = resolve(ks, members[members.length - 1]!);
    return 'missing' in r ? members[0]! : r.t;
  })();
  const s = STEP.name;
  return {
    kind: 'step',
    ...base,
    hora: s.hora,
    rule: {
      id: ANALYSIS_STEP_IDS.name,
      card: ks.goal === 'differentiate' ? CARD_D : CARD_P,
      label: `Nommer ${head.base}`,
      hora: s.hora,
      why: s.why,
    },
    amove: { kind: 'name', base: head.base, members, value: def },
  };
}

/** u′(x) = … : la dérivée de la partie nommée */
function judgeDeriv(ks: KAnaState, base: Base, head: Head, members: Term[]): KAnaVerdict {
  const n = ks.names.get(head.base)!;
  const d = safeDerive(n.def, ks.x);
  if (!d) return { kind: 'unjustified', ...base, title: 'Je ne sais pas dériver ça', message: '' };
  const want = vecOf(ks, d);
  for (const m of members) {
    const v = vecR(ks, m)!;
    if (!same(v, want)) {
      const r = vecRatio(v, want);
      return {
        kind: 'false',
        ...base,
        title: r !== null && closeTo(r, -1) ? 'Le signe' : 'Dérivée fausse',
        message: `Ce n’est pas la dérivée de ${head.base}(${ks.x}).`,
      };
    }
  }
  if (ks.derivs.has(head.base))
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: `${head.base}′(${ks.x}) est déjà écrite.`,
    };
  const tm = templateMatch(ks, [n.def], members[0]!);
  if ('fail' in tm)
    return {
      kind: 'unjustified',
      ...base,
      title: 'Montre le calcul',
      message: RULE_HINT[tm.fail[0] ?? 'usual'],
    };
  if (tm.nested.length) return nestedVerdict(ks, base, tm.nested[0]!);
  const kinds = tm.rules;
  const usualOnly = kinds.length === 1 && kinds[0] === 'usual';
  let hora = usualOnly ? 1 : kinds.reduce((s, k) => s + (k === 'usual' ? 0 : STEP[k].hora), 0);
  const steps: StepKind[] = [];
  for (let i = 1; i < members.length; i++) {
    if (sameWriting(members[i - 1]!, members[i]!)) continue;
    steps.push('rewrite');
    hora += 1;
  }
  const main: StepKind = usualOnly ? 'usual' : kinds.find((k) => k !== 'usual')!;
  const r = resolve(ks, members[members.length - 1]!);
  return {
    kind: 'step',
    ...base,
    hora,
    rule: {
      id: ANALYSIS_STEP_IDS[main],
      card: CARD_D,
      label: usualOnly ? `Dériver ${head.base}` : STEP[main].label,
      hora,
      why: [...new Set([main, ...steps])].map((k) => STEP[k].why).join(' '),
    },
    amove: {
      kind: 'deriv',
      base: head.base,
      members,
      value: 'missing' in r ? d : r.t,
    },
  };
}

function nestedVerdict(ks: KAnaState, base: Base, part: Term): KAnaVerdict {
  return {
    kind: 'unjustified',
    ...base,
    title: 'Une dérivée à la fois',
    message: `Deux règles d’un coup : nomme d’abord ${termTextOf(part)} (v(${ks.x}) = …) et écris sa dérivée.`,
  };
}

const termTextOf = (t: Term): string => termText(t);

/** f(x) = … : f réécrite */
function judgeF(ks: KAnaState, base: Base, members: Term[]): KAnaVerdict {
  for (const m of members) {
    const v = vecR(ks, m)!;
    if (!same(v, ks.fVec))
      return trapOr(ks, 'f', v, base, {
        title: `Ce n’est pas ${ks.fBase}`,
        message: `Ta ligne ne donne pas la même fonction que ${ks.fBase} : vérifie le calcul.`,
      });
  }
  let prev = ks.fForms[ks.fForms.length - 1]!;
  const labels: string[] = [];
  let hora = 0;
  for (const m of members) {
    const r = resolve(ks, m);
    const t = 'missing' in r ? m : r.t;
    if (ks.fForms.some((f) => sameWriting(f, t)) || sameWriting(prev, t)) {
      prev = t;
      continue;
    }
    labels.push(rewriteLabel(prev, t));
    hora += 1;
    prev = t;
  }
  if (!hora)
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: `Cette écriture de ${ks.fBase} est déjà là.`,
    };
  const label = labels.length === 1 ? labels[0]! : `Réécrire ${ks.fBase}`;
  return {
    kind: 'step',
    ...base,
    hora,
    rule: {
      id: ANALYSIS_STEP_IDS.rewrite,
      card: ks.goal === 'differentiate' ? CARD_D : CARD_P,
      label,
      hora,
      why: `${ks.fBase} écrite autrement : souvent, une forme plus simple à ${ks.goal === 'differentiate' ? 'dériver' : 'intégrer'}.`,
    },
    amove: {
      kind: 'f',
      base: ks.fBase,
      members: members.map((m) => {
        const r = resolve(ks, m);
        return 'missing' in r ? m : r.t;
      }),
    },
  };
}

function rewriteLabel(from: Term, to: Term): string {
  const a = termsOf(from).length;
  const b = termsOf(to).length;
  if (a === 1 && b > 1 && from.k === 'div') return 'Séparer la fraction';
  if (a === 1 && b > 1) return 'Développer';
  if (b === 1 && a > 1) return to.k === 'div' ? 'Réduire au même dénominateur' : 'Factoriser';
  if (b > a) return 'Développer';
  if (b < a) return 'Réduire';
  return 'Simplifier';
}

/** f′(x) = … */
function judgeTargetD(ks: KAnaState, base: Base, members: Term[]): KAnaVerdict {
  // chaque membre vaut f′
  for (const m of members) {
    const v = vecR(ks, m)!;
    if (!same(v, ks.want)) return trapOr(ks, 'target', v, base, wrongDerivative(ks, v));
  }
  const steps: StepKind[] = [];
  const labels: string[] = [];
  let prev: Term | null = ks.targets.length ? ks.targets[ks.targets.length - 1]! : null;
  let ruled = false;
  for (const E of members) {
    if (prev === null) {
      const tm = templateMatch(ks, ks.fForms, E);
      if ('fail' in tm)
        return {
          kind: 'unjustified',
          ...base,
          title: 'Montre le calcul',
          message: RULE_HINT[tm.fail.find((r) => r !== 'usual') ?? tm.fail[0] ?? 'usual'],
        };
      if (tm.nested.length) return nestedVerdict(ks, base, tm.nested[0]!);
      let rules = tm.rules.length > 1 ? tm.rules.filter((r) => r !== 'usual') : tm.rules;
      // la formule écrite avec les noms dit la règle appliquée : (uv)′, (u ÷ v)′, (1 ÷ v)′,
      // (g(u))′ — même quand f se dérivait aussi « à l'usuelle » (u = v = 3x − 2)
      const named = new Set(
        namesIn(ks, E)
          .map((h) => h.base)
          .filter((b) => b !== ks.fBase),
      );
      if (rules.length === 1 && rules[0] === 'usual' && named.size)
        rules = [
          named.size >= 2 ? (numDen(E) ? 'quotient' : 'product') : numDen(E) ? 'inverse' : 'chain',
        ];
      steps.push(...rules);
      labels.push(
        rules.length === 1 && rules[0] === 'usual' && tm.affine
          ? 'Dériver g(ax + b)'
          : STEP[rules[0]!].label,
      );
      ruled = true;
    } else {
      const r = rewriteStep(ks, prev, E);
      if (r === 'same') {
        prev = E;
        continue;
      }
      if (r === 'jump')
        return {
          kind: 'unjustified',
          ...base,
          title: 'Une étape à la fois',
          message: 'Remplace d’abord chaque nom par son expression, puis fais les calculs.',
        };
      if (r === 'names')
        return {
          kind: 'unjustified',
          ...base,
          title: 'Dans l’ordre',
          message: 'Les noms (u, v…) s’écrivent dans la formule, avant de remplacer.',
        };
      steps.push(r === 'substitute' ? 'substitute' : 'rewrite');
      labels.push(r === 'substitute' ? STEP.substitute.label : r);
    }
    prev = E;
  }
  if (!steps.length)
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'Cette ligne redit ce qui est déjà écrit.',
    };
  const last = members[members.length - 1]!;
  const explicit = namesIn(ks, last).length === 0;
  const final = !ks.concluded && explicit && termSize(last) <= ks.answerSize;
  return stepVerdict(ks, base, steps, labels, final, {
    kind: 'target',
    base: ks.tBase,
    members,
    final,
    ruled,
  });
}

/** F(x) = … : une primitive */
function judgeTargetP(ks: KAnaState, base: Base, members: Term[]): KAnaVerdict {
  const cond = ks.point?.kind === 'condition' ? ks.point : null;
  const resolved: Term[] = [];
  for (const m of members) {
    const r = resolve(ks, m);
    const t = 'missing' in r ? m : r.t;
    resolved.push(t);
    const d = safeDerive(t, ks.x);
    const dv = d ? vecOf(ks, d) : null;
    if (!dv || !same(dv, ks.fVec)) {
      const v = vecOf(ks, t);
      return trapOr(ks, 'target', v, base, wrongPrimitive(ks, dv ?? v, v));
    }
    // moteur v44 : sans C, la primitive doit vérifier la condition
    if (cond && !hasC(t) && !condHolds(ks, t))
      return trapOr(ks, 'target', vecOf(ks, t), base, condMiss(ks, t));
  }
  const steps: StepKind[] = [];
  const labels: string[] = [];
  // la dernière primitive écrite : telle qu'écrite (noms compris) et noms remplacés
  let prevRaw: Term | null = ks.targets.length ? ks.targets[ks.targets.length - 1]! : null;
  let prev: Term | null = prevRaw ? resolvedOr(ks, prevRaw) : null;
  for (let i = 0; i < members.length; i++) {
    const E = resolved[i]!;
    const raw = members[i]!;
    if (prev && prevRaw && same(vecOf(ks, prev), vecOf(ks, E))) {
      if (namesIn(ks, prevRaw).length && !namesIn(ks, raw).length) {
        steps.push('substitute');
        labels.push(STEP.substitute.label);
      } else if (!sameWriting(prevRaw, raw)) {
        steps.push('rewrite');
        labels.push(rewriteLabel(prev, E));
      }
      prev = E;
      prevRaw = raw;
      continue;
    }
    // moteur v44 : la constante C remplacée par sa valeur (« F(x) = sin(x) + 2 »)
    if (
      cond &&
      prev &&
      hasC(prev) &&
      !hasC(E) &&
      vecOffset(vecOf(ks, E), vecOf(ks, prev)) !== null
    ) {
      if (cond.c !== null) {
        steps.push('substitute');
        labels.push('Remplacer C');
      } else {
        steps.push('condition');
        labels.push(`Utiliser ${cond.condText}`);
        if (!cond.values.has(cond.condHead)) {
          steps.push('point');
          labels.push(`Calculer ${cond.condHead}`);
        }
      }
      prev = E;
      prevRaw = raw;
      continue;
    }
    // une primitive : la dérivée de E, morceau par morceau, redonne f terme à terme
    const tpl = stepTemplate(E, ks.x, () => null);
    const ok =
      !!tpl &&
      ks.fForms.some((form) => {
        const fv = termVecs(ks, form);
        if (!fv) return false;
        const pieces = tpl.pieces.map((p) => vecOf(ks, p));
        const want = tpl.den ? pieces.map((p) => divide(p, vecOf(ks, tpl.den!))) : pieces;
        return groupsMatch(
          fv,
          want.filter((p) => !isZeroVec(p)),
        );
      });
    if (!ok)
      return {
        kind: 'unjustified',
        ...base,
        title: 'Montre comment',
        message: `Écris d’abord ${ks.fBase} comme une somme de formes connues (xⁿ, u′eᵘ, u′ ÷ u, u′uⁿ…).`,
      };
    const usual = tpl!.rules.every((r) => r === 'usual');
    steps.push(usual ? 'primitive' : 'recognize');
    labels.push(usual ? STEP.primitive.label : recognizeLabel(tpl!.rules));
    // moteur v44 : une primitive sans C qui vérifie la condition (la constante choisie)
    if (cond && !hasC(E)) {
      steps.push('condition');
      labels.push(`Utiliser ${cond.condText}`);
    }
    prev = E;
    prevRaw = raw;
  }
  if (!steps.length)
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'Cette primitive est déjà écrite.',
    };
  const last = resolved[resolved.length - 1]!;
  const explicit = namesIn(ks, members[members.length - 1]!).length === 0;
  // une condition : la famille « … + C » n'est jamais la réponse
  const final =
    !ks.concluded &&
    explicit &&
    termSize(dropConstants(last, ks.x)) <= ks.answerSize &&
    (!cond || !hasC(last));
  return stepVerdict(ks, base, steps, labels, final, {
    kind: 'target',
    base: ks.tBase,
    members,
    final,
    ruled: true,
  });
}

function recognizeLabel(rules: StepRule[]): string {
  const r = rules.find((x) => x !== 'usual');
  switch (r) {
    case 'chain_exp':
      return 'Reconnaître u′eᵘ';
    case 'chain_ln':
      return 'Reconnaître u′ ÷ u';
    case 'chain_pow':
      return 'Reconnaître u′uⁿ';
    case 'chain_sqrt':
      return 'Reconnaître u′ ÷ √u';
    case 'chain_trig':
      return 'Reconnaître u′ cos u';
    case 'chain_arc':
      return 'Reconnaître u′ ÷ (1 + u²)';
    default:
      return STEP.recognize.label;
  }
}

/** passer de l'écriture validée `prev` à E (même fonction) */
function rewriteStep(
  ks: KAnaState,
  prev: Term,
  E: Term,
): 'same' | 'substitute' | 'jump' | 'names' | string {
  if (sameWriting(prev, E)) return 'same';
  const pn = new Set(namesIn(ks, prev).map(nameKey));
  const en = namesIn(ks, E).map(nameKey);
  if (en.some((n) => !pn.has(n))) return 'names';
  if (pn.size && en.length < pn.size) {
    // remplacer : chaque morceau de la formule devient un groupe de termes
    const pt = termVecs(ks, prev);
    const et = termVecs(ks, E);
    const pnd = numDen(prev);
    const end = numDen(E);
    if (pnd && end) {
      const pd = vecR(ks, pnd.d);
      const ed = vecR(ks, end.d);
      const r = pd && ed ? vecRatio(ed, pd) : null;
      const pnt = termVecs(ks, pnd.n);
      const ent = termVecs(ks, end.n);
      if (
        r !== null &&
        !closeTo(r, 0) &&
        pnt &&
        ent &&
        groupsMatch(
          ent.map((v) => scale(v, 1 / r)),
          pnt,
        )
      )
        return 'substitute';
      return 'jump';
    }
    if (pt && et && groupsMatch(et, pt)) return 'substitute';
    return 'jump';
  }
  if (pn.size) return 'jump';
  return rewriteLabel(prev, E);
}

function stepVerdict(
  ks: KAnaState,
  base: Base,
  steps: StepKind[],
  labels: string[],
  final: boolean,
  amove: AMove,
  card?: string,
): KAnaVerdict {
  const kinds = [...new Set(steps)];
  const hora = steps.reduce((s, k) => s + STEP[k].hora, 0);
  const main = steps.reduce((m, k) => (STEP[k].hora > STEP[m].hora ? k : m), steps[0]!);
  const label = labels[steps.indexOf(main)] ?? STEP[main].label;
  const why = kinds.map((k) => STEP[k].why).join(' ') + (final ? ' C’est la réponse.' : '');
  return {
    kind: 'step',
    ...base,
    hora,
    final,
    rule: {
      id: ANALYSIS_STEP_IDS[main],
      card: card ?? (ks.goal === 'differentiate' ? CARD_D : CARD_P),
      label,
      hora,
      why,
    },
    amove,
  };
}

/* ------------------------------ en un point (moteur v44) ------------------------------ */

const hasC = (t: Term): boolean => symbolsOf(t).has('C');

/** la valeur de t (sans x) pour C = C_VALUE et C = C_ALT ; null si elle ne se calcule pas */
function constPair(
  ks: KAnaState,
  t: Term,
  at?: number,
  extra?: Record<string, number>,
): [number, number] | null {
  if (at === undefined && symbolsOf(t).has(ks.x)) return null;
  const env = { ...ks.env, ...extra, ...(at === undefined ? {} : { [ks.x]: at }) };
  try {
    const a = evalFloat(t, { ...env, C: C_VALUE });
    const b = evalFloat(t, { ...env, C: C_ALT });
    return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
  } catch {
    return null;
  }
}

const samePair = (p: [number, number], q: [number, number]): boolean =>
  closeTo(p[0], q[0]) && closeTo(p[1], q[1]);

/** une primitive sans C vérifie-t-elle la condition ? */
function condHolds(ks: KAnaState, t: Term): boolean {
  const p = ks.point!;
  const v = constPair(ks, t, p.a);
  return !!v && closeTo(v[0], p.value);
}

function condMiss(ks: KAnaState, t: Term): Diag {
  const p = ks.point!;
  const hasConst = termList(t).some((c) => !symbolsOf(c).has(ks.x));
  if (!hasConst)
    return {
      title: 'Il manque la constante',
      message: `C’est une primitive, mais ${p.condHead} ne vaut pas ${p.valueText} : écris « ${ks.tBase}(${ks.x}) = … + C », puis calcule C.`,
    };
  const v = constPair(ks, t, p.a);
  return {
    title: 'La constante',
    message: `Avec cette constante, ${p.condHead} = ${v ? formatNumber(v[0]) : '?'} et non ${p.valueText} : calcule C avec ${p.condText}.`,
  };
}

/** la dernière famille de primitives écrite (« F(x) = sin(x) + C »), noms remplacés */
function familyTarget(ks: KAnaState): Term | null {
  for (let i = ks.targets.length - 1; i >= 0; i--) {
    const t = resolvedOr(ks, ks.targets[i]!);
    if (hasC(t)) return t;
  }
  return null;
}

function familyMissing(ks: KAnaState, base: Base): KAnaVerdict | null {
  if (familyTarget(ks)) return null;
  if (ks.targets.length)
    return {
      kind: 'unjustified',
      ...base,
      title: 'Ajoute la constante',
      message: `Les primitives de ${ks.fBase} s’écrivent « ${ks.tBase}(${ks.x}) = … + C » : écris-les avec C.`,
    };
  return {
    kind: 'unjustified',
    ...base,
    title: `Écris d’abord ${ks.tBase}(${ks.x})`,
    message: `Écris d’abord les primitives : « ${ks.tBase}(${ks.x}) = … + C ».`,
  };
}

/** la constante C qui fait vérifier la condition à la famille (null : pas de C) */
function solveC(ks: KAnaState, fam: Term): number | null {
  const p = ks.point!;
  const v = constPair(ks, fam, p.a);
  if (!v) return null;
  const slope = (v[1] - v[0]) / (C_ALT - C_VALUE);
  if (Math.abs(slope) < 1e-12) return null;
  return C_VALUE + (p.value - v[0]) / slope;
}

type PointLine = { kind: 'value'; key: string; primes: number } | { kind: 'y' } | { kind: 'c' };

/** la ligne est-elle une ligne « en un point » (« f(0) = … », « y = … », « C = … ») ? */
function pointLineKind(ks: KAnaState, atom: string | undefined): PointLine | null {
  const p = ks.point;
  if (!p || !atom) return null;
  if (p.kind === 'tangent' && atom === 'y') return { kind: 'y' };
  if (p.kind === 'condition' && atom === 'C') return { kind: 'c' };
  const m = NAME_RE.exec(atom);
  if (!m || m[3] !== p.at) return null;
  const primes = m[2]!.length;
  if (p.kind === 'tangent' && m[1] === ks.fBase && primes <= 1)
    return { kind: 'value', key: atom, primes };
  if (p.kind === 'condition' && atom === p.condHead) return { kind: 'value', key: atom, primes };
  return null;
}

function readPointMembers(ks: KAnaState, A: string[]): Term[] {
  const line = parseLine(normalize(caretAtoms(A)), { chains: true });
  if (line.dis.length !== 1) throw new ReadError('Une seule égalité par ligne.');
  const eq = line.dis[0]!;
  const h = eq.sides[0];
  if (
    eq.cmp ||
    eq.chain ||
    eq.rels ||
    eq.mod !== undefined ||
    eq.sides.length < 2 ||
    !h ||
    h.k !== 's' ||
    h.n !== A[0]
  )
    throw new ReadError(`Écris une égalité « ${A[0]} = … ».`);
  try {
    return eq.sides.slice(1).map((s) => fromNode(s));
  } catch (e) {
    if (e instanceof UnsupportedTerm) throw new ReadError('Ce calcul ne se lit pas.');
    throw e;
  }
}

function judgePointLine(
  ks: KAnaState,
  chips: Chip[],
  pk: PointLine,
  opts?: { verify?: boolean },
): KAnaVerdict {
  const A = chips.flatMap((c) => c.atoms);
  const prefix = strictPrefixOfKnown(ks, A);
  let members: Term[];
  try {
    members = readPointMembers(ks, A);
  } catch (e) {
    if (!opts?.verify) return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
    if (e instanceof ReadError)
      return {
        kind: 'syntax',
        atoms: A,
        line: lineOf(A),
        title: 'Une égalité',
        message: e.message,
      };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça ne se lit pas',
      message: 'Termine ta ligne : il manque un morceau.',
      badChip: chips.length - 1,
    };
  }
  const base: Base = { atoms: A, line: lineOf(A) };
  const v = ks.givenTexts.has(kanaText(A))
    ? ({
        kind: 'useless',
        ...base,
        title: 'C’est l’énoncé',
        message: 'Cette ligne est dans l’énoncé : écris la suivante.',
      } as KAnaVerdict)
    : pk.kind === 'value'
      ? judgeValue(ks, base, pk, members)
      : pk.kind === 'c'
        ? judgeC(ks, base, members)
        : judgeY(ks, base, members);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}

/** la valeur attendue d'une tête « f(0) », « f′(0) », « F(0) » ; null : pas encore calculable */
function pointWant(ks: KAnaState, pk: { primes: number }): [number, number] | null {
  const p = ks.point!;
  if (p.kind === 'tangent') return pk.primes ? [p.fpa, p.fpa] : [p.fa, p.fa];
  const fam = familyTarget(ks);
  return fam ? constPair(ks, fam, p.a) : null;
}

function pointCard(ks: KAnaState): string {
  return ks.point?.kind === 'tangent' ? CARD_T : CARD_P;
}

/** f(a) = …, f′(a) = …, F(a) = … : une valeur en un point */
function judgeValue(
  ks: KAnaState,
  base: Base,
  pk: { key: string; primes: number },
  members: Term[],
): KAnaVerdict {
  const p = ks.point!;
  if (p.kind === 'tangent' && pk.primes === 1 && !ks.concluded)
    return {
      kind: 'unjustified',
      ...base,
      title: `Écris d’abord ${ks.fBase}′(${ks.x})`,
      message: `${pk.key} se calcule avec l’expression de ${ks.fBase}′ : dérive d’abord ${ks.fBase}.`,
    };
  if (p.kind === 'condition') {
    const miss = familyMissing(ks, base);
    if (miss) return miss;
  }
  const want = pointWant(ks, pk)!;
  for (const m of members) {
    if (symbolsOf(m).has(ks.x))
      return {
        kind: 'unjustified',
        ...base,
        title: `Remplace ${ks.x}`,
        message: `Dans ${pk.key}, ${ks.x} vaut ${p.at} : remplace-le partout.`,
      };
    const v = constPair(ks, m);
    if (!v || !samePair(v, want)) return { kind: 'false', ...base, ...wrongValue(ks, pk, v) };
  }
  if (p.values.has(pk.key))
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: `${pk.key} est déjà calculé.`,
    };
  const s = STEP.point;
  return {
    kind: 'step',
    ...base,
    hora: s.hora,
    rule: {
      id: ANALYSIS_STEP_IDS.point,
      card: pointCard(ks),
      label: `Calculer ${pk.key}`,
      hora: s.hora,
      why: s.why,
    },
    amove: { kind: 'value', base: pk.key, members, pair: want },
  };
}

function wrongValue(
  ks: KAnaState,
  pk: { key: string; primes: number },
  v: [number, number] | null,
): Diag {
  const p = ks.point!;
  if (p.kind === 'tangent') {
    const fk = `${ks.fBase}(${p.at})`;
    const fpk = `${ks.fBase}′(${p.at})`;
    if (v && !closeTo(p.fa, p.fpa)) {
      if (pk.primes === 0 && closeTo(v[0], p.fpa))
        return {
          title: `C’est ${fpk}`,
          message: `${fk} se calcule avec l’expression de ${ks.fBase}, pas avec celle de ${ks.fBase}′.`,
        };
      if (pk.primes === 1 && closeTo(v[0], p.fa))
        return {
          title: `C’est ${fk}`,
          message: `${fpk} se calcule avec l’expression de ${ks.fBase}′ (la dérivée), pas avec celle de ${ks.fBase}.`,
        };
    }
    return {
      title: `Ce n’est pas ${pk.key}`,
      message: `Remplace ${ks.x} par ${p.at} dans ${ks.fBase}${pk.primes ? '′' : ''}(${ks.x}), puis calcule.`,
    };
  }
  return {
    title: `Ce n’est pas ${pk.key}`,
    message: `Remplace ${ks.x} par ${p.at} dans ta primitive « ${ks.tBase}(${ks.x}) = … + C » : C reste C.`,
  };
}

/** C = … : la condition fixe la constante */
function judgeC(ks: KAnaState, base: Base, members: Term[]): KAnaVerdict {
  const p = ks.point!;
  const miss = familyMissing(ks, base);
  if (miss) return miss;
  const c = solveC(ks, familyTarget(ks)!);
  if (c === null)
    return {
      kind: 'unjustified',
      ...base,
      title: 'Où est C ?',
      message: `${p.condHead} ne dépend pas de C : écris « ${ks.tBase}(${ks.x}) = … + C ».`,
    };
  for (const m of members) {
    if (symbolsOf(m).has(ks.x) || hasC(m))
      return {
        kind: 'unjustified',
        ...base,
        title: 'Une valeur',
        message: 'Écris la valeur de C : « C = … ».',
      };
    const v = constPair(ks, m);
    if (!v || !closeTo(v[0], c))
      return {
        kind: 'false',
        ...base,
        title: 'Pas cette valeur',
        message: `Avec C = ${v ? formatNumber(v[0]) : '?'}, ${p.condHead} ne vaut pas ${p.valueText} : écris ${p.condHead} avec ta primitive, égale-le à ${p.valueText}, puis résous.`,
      };
  }
  if (p.c !== null && closeTo(p.c, c))
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'C est déjà trouvée.',
    };
  const steps: StepKind[] = ['condition'];
  const labels = [`Utiliser ${p.condText}`];
  if (!p.values.has(p.condHead)) {
    steps.push('point');
    labels.push(`Calculer ${p.condHead}`);
  }
  return stepVerdict(
    ks,
    base,
    steps,
    labels,
    false,
    { kind: 'c', base: 'C', members, pair: [c, c] },
    CARD_P,
  );
}

/** y = … : la tangente (formule, valeurs remplacées, réduite) */
function judgeY(ks: KAnaState, base: Base, members: Term[]): KAnaVerdict {
  const p = ks.point!;
  const fk = `${ks.fBase}(${p.at})`;
  const fpk = `${ks.fBase}′(${p.at})`;
  const penv = { ...ks.env, [fk]: p.fa, [fpk]: p.fpa };
  const vec = (t: Term): Vec => valuesAt(t, ks.x, ks.points, penv);
  const names = (t: Term): string[] => [...symbolsOf(t)].filter((n) => n === fk || n === fpk);
  for (const m of members) {
    const v = vec(m);
    if (!same(v, p.tangent)) return { kind: 'false', ...base, ...wrongTangent(ks, v) };
  }
  // la pente : f′(a), une fois f′(x) écrite (ou f′(a) calculé)
  const slopeKnown = ks.concluded || p.values.has(fpk);
  if (!slopeKnown && members.some((m) => !symbolsOf(m).has(fpk)))
    return {
      kind: 'unjustified',
      ...base,
      title: `Calcule d’abord ${fpk}`,
      message: `La pente de la tangente est ${fpk} : dérive ${ks.fBase}, puis calcule ${fpk}.`,
    };
  const steps: StepKind[] = [];
  const labels: string[] = [];
  let prev: Term | null = p.ys.length ? p.ys[p.ys.length - 1]! : null;
  for (const E of members) {
    if (prev === null) {
      const slope = ks.points.map((x) => p.fpa * (x - p.a));
      const value = ks.points.map(() => p.fa);
      if (!groupsMatch(termList(E).map(vec), [slope, value]))
        return {
          kind: 'unjustified',
          ...base,
          title: 'Écris la formule',
          message: `La tangente en ${p.at} : « y = ${fpk} × (${ks.x} − ${p.at}) + ${fk} », puis remplace.`,
        };
      steps.push('tangent');
      labels.push(STEP.tangent.label);
    } else {
      if (sameWriting(prev, E)) {
        prev = E;
        continue;
      }
      const pn = names(prev).length;
      const en = names(E).length;
      if (en > pn)
        return {
          kind: 'unjustified',
          ...base,
          title: 'Dans l’ordre',
          message: `${fk} et ${fpk} s’écrivent dans la formule, avant de remplacer.`,
        };
      if (en < pn) {
        if (!groupsMatch(termList(E).map(vec), termList(prev).map(vec)))
          return {
            kind: 'unjustified',
            ...base,
            title: 'Une étape à la fois',
            message: 'Remplace d’abord chaque nom par sa valeur, puis réduis.',
          };
        steps.push('substitute');
        labels.push(STEP.substitute.label);
      } else {
        steps.push('rewrite');
        labels.push(termList(E).length > termList(prev).length ? 'Développer' : 'Réduire');
      }
    }
    prev = E;
  }
  if (!steps.length)
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'Cette ligne redit ce qui est déjà écrit.',
    };
  const last = members[members.length - 1]!;
  const final = !p.concluded && !names(last).length && termSize(last) <= p.answerSize;
  return stepVerdict(
    ks,
    base,
    steps,
    labels,
    final,
    { kind: 'y', base: 'y', members, final },
    CARD_T,
  );
}

function wrongTangent(ks: KAnaState, v: Vec): Diag {
  const p = ks.point!;
  const fk = `${ks.fBase}(${p.at})`;
  const fpk = `${ks.fBase}′(${p.at})`;
  const formula = `y = ${fpk}(${ks.x} − ${p.at}) + ${fk}`;
  const at = (g: (x: number) => number): Vec => ks.points.map(g);
  if (
    !closeTo(p.fpa, 0) &&
    same(
      v,
      at(() => p.fa),
    )
  )
    return { title: 'La pente', message: `La tangente a pour pente ${fpk} : ${formula}.` };
  if (
    !closeTo(p.a, 0) &&
    same(
      v,
      at((x) => p.fpa * x + p.fa),
    )
  )
    return { title: `${ks.x} − ${p.at}`, message: `${formula} : n’oublie pas « − ${p.at} ».` };
  if (
    same(
      v,
      at((x) => p.fa * (x - p.a) + p.fpa),
    )
  )
    return {
      title: 'Dans l’ordre',
      message: `La pente est ${fpk}, la valeur au point est ${fk} : ${formula}.`,
    };
  if (
    !closeTo(p.fa, 0) &&
    same(
      v,
      at((x) => p.fpa * (x - p.a)),
    )
  )
    return { title: `Il manque ${fk}`, message: `${formula} : ajoute ${fk}.` };
  return { title: 'Pas la tangente', message: `Ce n’est pas la tangente en ${p.at} : ${formula}.` };
}

function commitPoint(ks: KAnaState, v: KAnaVerdict): void {
  const m = v.amove!;
  const p = ks.point!;
  if (m.kind === 'value') p.values.set(m.base, m.pair!);
  if (m.kind === 'c') p.c = m.pair![0];
  if (m.kind === 'y') {
    p.ys.push(m.members[m.members.length - 1]!);
    if (m.final) p.concluded = true;
  }
  ks.lines.push({ text: kanaText(v.atoms) });
}

/** une ligne « en un point » est-elle vraie ? */
function pointHolds(ks: KAnaState, text: string, pk: PointLine): boolean {
  let members: Term[];
  try {
    members = readPointMembers(ks, kanaLineAtoms(text));
  } catch {
    return false;
  }
  const p = ks.point!;
  if (pk.kind === 'y') {
    const penv = {
      ...ks.env,
      [`${ks.fBase}(${p.at})`]: p.fa,
      [`${ks.fBase}′(${p.at})`]: p.fpa,
    };
    return members.every((m) => same(valuesAt(m, ks.x, ks.points, penv), p.tangent));
  }
  if (pk.kind === 'c') {
    const fam = familyTarget(ks);
    const c = fam ? solveC(ks, fam) : null;
    return (
      c !== null &&
      members.every((m) => {
        const v = constPair(ks, m);
        return !!v && closeTo(v[0], c);
      })
    );
  }
  const want = pointWant(ks, pk);
  return (
    !!want &&
    members.every((m) => {
      const v = constPair(ks, m);
      return !!v && samePair(v, want);
    })
  );
}

/** une ligne « en un point » déjà établie */
function pointEstablished(ks: KAnaState, text: string, pk: PointLine): boolean {
  const p = ks.point!;
  if (pk.kind === 'value') return p.values.has(pk.key);
  if (pk.kind === 'c') return p.c !== null;
  try {
    const members = readPointMembers(ks, kanaLineAtoms(text));
    const last = members[members.length - 1]!;
    // même écriture, au calcul près (« y = x + 1 » n'est pas établie par « y = 1 × x + 1 »)
    return p.ys.some((t) => sameWriting(t, last));
  } catch {
    return false;
  }
}

/* ------------------------------ validation d'une ligne ------------------------------ */

function commitStage(ks: KAnaState, v: KAnaVerdict): void {
  const m = v.amove;
  if (!m) return;
  switch (m.kind) {
    case 'name':
      ks.names.set(m.base, { def: m.value!, vec: vecOf(ks, m.value!) });
      break;
    case 'deriv':
      ks.derivs.set(m.base, { expr: m.value!, vec: vecOf(ks, m.value!) });
      break;
    case 'f':
      m.members.forEach((t) => {
        if (!ks.fForms.some((f) => termKey(canon(f)) === termKey(canon(t)))) ks.fForms.push(t);
      });
      break;
    case 'target':
      ks.targets.push(m.members[m.members.length - 1]!);
      if (m.final) ks.concluded = true;
      break;
  }
  ks.lines.push({ text: kanaText(v.atoms) });
}

/* ------------------------------ dérivée seconde : deux étapes ------------------------------ */

/** l'étape qui juge une ligne de tête `primes` (dérivée seconde) */
function stageOf(ks: KAnaState, primes: number): KAnaState {
  if (!ks.second || primes === 0) return ks;
  return primes === 2 || ks.concluded ? ks.second : ks;
}

const atomPrimes = (ks: KAnaState, atom: string | undefined): number => {
  const m = NAME_RE.exec(atom ?? '');
  return m && m[1] === ks.fBase && m[3] === ks.x ? m[2]!.length : -1;
};

/** moteur v44 : une feuille « en un point » (tangente, primitive avec condition) */
function judgePointSheet(ks: KAnaState, chips: Chip[], opts?: { verify?: boolean }): KAnaVerdict {
  const p = ks.point!;
  const pk = pointLineKind(ks, chips[0]?.atoms[0]);
  if (pk) return judgePointLine(ks, chips, pk, opts);
  const v = judgeStage(ks, chips, opts);
  if (p.kind !== 'tangent' || !v.final) return v;
  // la dérivée trouvée ne finit pas la feuille : la tangente reste à écrire
  return {
    ...v,
    final: false,
    ...(v.rule && {
      rule: {
        ...v.rule,
        why: v.rule.why.replace(
          ' C’est la réponse.',
          ` Calcule maintenant ${ks.fBase}(${p.at}) et ${ks.fBase}′(${p.at}).`,
        ),
      },
    }),
  };
}

/** juge une ligne ; une dérivée seconde la confie à son étape */
export function kanaJudge(ks: KAnaState, chips: Chip[], opts?: { verify?: boolean }): KAnaVerdict {
  if (ks.arcs && !arcsActive()) return inArcs(ks, () => kanaJudge(ks, chips, opts));
  if (ks.point) return judgePointSheet(ks, chips, opts);
  if (!ks.second) return judgeStage(ks, chips, opts);
  const A = chips.flatMap((c) => c.atoms);
  const primes = atomPrimes(ks, A[0]);
  const stage = stageOf(ks, primes);
  const v = judgeStage(stage, chips, opts);
  if (stage === ks) {
    // la 1re étape ne finit pas la feuille : f′ écrit, on la dérive à son tour
    if (!v.final) return v;
    return {
      ...v,
      final: false,
      ...(v.rule && {
        rule: {
          ...v.rule,
          why: v.rule.why.replace(
            ' C’est la réponse.',
            ` Dérive-la à son tour : ${ks.fBase}′′ est la dérivée de ${ks.fBase}′.`,
          ),
        },
      }),
    };
  }
  // « f′′(x) = … » juste avant f′ : gardée sans HORA
  if (!ks.concluded && (v.kind === 'step' || v.kind === 'ready')) {
    if (!opts?.verify) return { kind: 'pending', atoms: A, complete: true };
    return {
      kind: 'useless',
      atoms: A,
      line: lineOf(A),
      complete: true,
      title: `Écris d’abord ${ks.fBase}′(${ks.x})`,
      message: `Ta ligne est juste, mais ${ks.fBase}′′ est la dérivée de ${ks.fBase}′ : écris ${ks.fBase}′(${ks.x}) = …, puis dérive-la. Tu peux garder cette ligne sans HORA.`,
      stage: 2,
    };
  }
  return { ...v, stage: 2 };
}

export function kanaCommit(ks: KAnaState, v: KAnaVerdict): void {
  if (ks.arcs && !arcsActive()) return inArcs(ks, () => kanaCommit(ks, v));
  const k = v.amove?.kind;
  if (ks.point && (k === 'value' || k === 'c' || k === 'y')) {
    commitPoint(ks, v);
    return;
  }
  if (ks.second && v.stage === 2) {
    commitStage(ks.second, v);
    if (v.amove) ks.lines.push({ text: kanaText(v.atoms) });
    return;
  }
  commitStage(ks, v);
  // une écriture de f′ de la 1re étape : une écriture de la fonction de la 2e
  const m = v.amove;
  if (ks.second && m?.kind === 'target') {
    const t = m.members[m.members.length - 1]!;
    if (!ks.second.fForms.some((f) => termKey(canon(f)) === termKey(canon(t))))
      ks.second.fForms.push(t);
  }
}

/** la ligne est vraie (cible : la bonne dérivée / une primitive ; nom : une égalité) */
export function kanaHolds(ks: KAnaState, text: string): boolean {
  if (ks.arcs && !arcsActive()) return inArcs(ks, () => kanaHolds(ks, text));
  const pk = pointLineKind(ks, kanaLineAtoms(text)[0]);
  if (pk) return pointHolds(ks, text, pk);
  if (!ks.second) return holdsStage(ks, text);
  const primes = headPrimes(text, ks.fBase, ks.x);
  if (primes === 2) return holdsStage(ks.second, text);
  return holdsStage(ks, text) || (ks.concluded && holdsStage(ks.second, text));
}

/** la ligne donne la réponse : une ligne cible, juste, sans nom (tangente : « y = … ») */
export function kanaIsAnswer(ks: KAnaState, text: string): boolean {
  if (ks.arcs && !arcsActive()) return inArcs(ks, () => kanaIsAnswer(ks, text));
  const p = ks.point;
  if (p?.kind === 'tangent') {
    const pk = pointLineKind(ks, kanaLineAtoms(text)[0]);
    if (pk?.kind !== 'y' || !pointHolds(ks, text, pk)) return false;
    try {
      const members = readPointMembers(ks, kanaLineAtoms(text));
      const names = new Set([`${ks.fBase}(${p.at})`, `${ks.fBase}′(${p.at})`]);
      return members.every((m) => ![...symbolsOf(m)].some((n) => names.has(n)));
    } catch {
      return false;
    }
  }
  return isAnswerStage(ks.second ?? ks, text);
}

/** une ligne déjà établie (même tête, même écriture ; un nom : même fonction) */
export function kanaEstablished(ks: KAnaState, text: string): boolean {
  if (ks.arcs && !arcsActive()) return inArcs(ks, () => kanaEstablished(ks, text));
  const pk = pointLineKind(ks, kanaLineAtoms(text)[0]);
  if (pk) return pointEstablished(ks, text, pk);
  if (!ks.second) return establishedStage(ks, text);
  if (headPrimes(text, ks.fBase, ks.x) === 2) return establishedStage(ks.second, text);
  return establishedStage(ks, text) || (ks.concluded && establishedStage(ks.second, text));
}

/** coup de pouce : la ligne du corrigé qui suit la dernière ligne déjà écrite */
export function kanaNextMove(
  ks: KAnaState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (ks.arcs && !arcsActive()) return inArcs(ks, () => kanaNextMove(ks));
  if (ks.point) {
    if (kanaGoalReached(ks)) return null;
    // l'élève a pu écrire la formule de la tangente avant de dériver : le corrigé se reprend
    // au début si rien ne suit sa dernière ligne
    return nextMoveOf(
      ks,
      (t) => kanaEstablished(ks, t),
      (c) => kanaJudge(ks, c, { verify: true }),
      true,
    );
  }
  return nextMoveStage(ks.second && ks.concluded ? ks.second : ks);
}

export function kanaGoalReached(ks: KAnaState): boolean {
  if (ks.point?.kind === 'tangent') return ks.point.concluded;
  return (ks.second ?? ks).concluded;
}

function readText(ks: KAnaState, text: string): Read | null {
  try {
    return readAtoms(ks, kanaLineAtoms(text));
  } catch {
    return null;
  }
}

/** la ligne est vraie (cible : la bonne dérivée / une primitive ; nom : une égalité) */
function holdsStage(ks: KAnaState, text: string): boolean {
  const r = readText(ks, text);
  if (!r) return false;
  const kind = kindOf(ks, r.head);
  return r.members.every((m) => {
    const res = resolve(ks, m);
    if ('missing' in res) return false;
    if (kind === 'target') {
      if (ks.goal === 'differentiate') return same(vecOf(ks, res.t), ks.want);
      const d = safeDerive(res.t, ks.x);
      if (!d || !same(vecOf(ks, d), ks.fVec)) return false;
      // moteur v44 : sans C, la condition doit être vérifiée
      return ks.point?.kind !== 'condition' || hasC(res.t) || condHolds(ks, res.t);
    }
    if (kind === 'f') return same(vecOf(ks, res.t), ks.fVec);
    const n = resolveName(ks, r.head);
    return !!n && same(vecOf(ks, res.t), vecOf(ks, n));
  });
}

/** la ligne donne la réponse : une ligne cible, juste, sans nom */
function isAnswerStage(ks: KAnaState, text: string): boolean {
  const r = readText(ks, text);
  if (!r || kindOf(ks, r.head) !== 'target') return false;
  if (r.members.some((m) => namesIn(ks, m).length)) return false;
  // moteur v44 : la famille « … + C » n'est pas LA primitive cherchée
  if (ks.point?.kind === 'condition' && r.members.some((m) => hasC(resolvedOr(ks, m))))
    return false;
  return holdsStage(ks, text);
}

/** une ligne déjà établie (même tête, même écriture ; un nom : même fonction) */
function establishedStage(ks: KAnaState, text: string): boolean {
  const r = readText(ks, text);
  if (!r) return false;
  const last = r.members[r.members.length - 1]!;
  const kind = kindOf(ks, r.head);
  if (kind === 'name') {
    const n = ks.names.get(r.head.base);
    const v = vecR(ks, last);
    return !!n && !!v && same(n.vec, v);
  }
  if (kind === 'deriv') {
    const d = ks.derivs.get(r.head.base);
    const v = vecR(ks, last);
    return !!d && !!v && same(d.vec, v);
  }
  if (kind === 'f') {
    const res = resolve(ks, last);
    const t = 'missing' in res ? last : res.t;
    return ks.fForms.some((f) => termKey(canon(f)) === termKey(canon(t)));
  }
  if (ks.goal === 'antiderivative') {
    const key = termKey(canon(resolvedOr(ks, last)));
    return ks.targets.some((t) => termKey(canon(resolvedOr(ks, t))) === key);
  }
  const key = termKey(canon(last));
  return ks.targets.some((t) => termKey(canon(t)) === key);
}

const resolvedOr = (ks: KAnaState, t: Term): Term => {
  const r = resolve(ks, t);
  return 'missing' in r ? t : r.t;
};

/** coup de pouce : la ligne du corrigé qui suit la dernière ligne déjà écrite */
function nextMoveStage(
  ks: KAnaState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (ks.concluded) return null;
  return nextMoveOf(
    ks,
    (t) => establishedStage(ks, t),
    (c) => judgeStage(ks, c, { verify: true }),
  );
}

function nextMoveOf(
  ks: KAnaState,
  established: (text: string) => boolean,
  judge: (chips: Chip[]) => KAnaVerdict,
  wrap = false,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  let from = 0;
  ks.path.forEach((t, i) => {
    if (established(t)) from = i + 1;
  });
  const order = ks.path.map((_, i) => i).filter((i) => i >= from || wrap);
  if (wrap) order.sort((a, b) => Number(a < from) - Number(b < from) || a - b);
  for (const i of order) {
    const t = ks.path[i]!;
    if (established(t)) continue;
    const atoms = kanaLineAtoms(t);
    const v = judge(chipsOf(atoms));
    if (v.kind !== 'step') continue;
    return { line: kanaLine(t), text: kanaText(atoms), atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

export function kanaSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KAnaVerdict }[];
  reached: boolean;
} {
  const ks = createKAnaState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KAnaVerdict }[] = [];
  for (const t of model.kernel!.path ?? []) {
    const atoms = kanaLineAtoms(t);
    const v = kanaJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    kanaCommit(ks, v);
    lines.push({ line: kanaLine(t), text: kanaText(atoms), atoms, v });
  }
  return { lines, reached: kanaGoalReached(ks) };
}

/* ------------------------------ palette ------------------------------ */

/** opérations de l'analyse, dans l'ordre d'affichage (exposants utiles ajoutés après ²) */
export const ANALYSIS_OPS = [
  '+',
  '−',
  '×',
  '÷',
  '(',
  ')',
  '²',
  '^',
  '√',
  'exp',
  'ln',
  'cos',
  'sin',
  'tan',
  'π',
  '=',
];

/** les bouts : noms de fonctions (f(x), f′(x), u(x)…), x, C ; nombres triés ; opérations */
export function kanaPaletteParts(model: ScratchModel): {
  names: string[];
  numbers: string[];
  ops: string[];
} {
  const spec = model.kernel!;
  const texts = [
    spec.start,
    spec.answer,
    ...(spec.path ?? []),
    ...(model.traps ?? []).map((t) => t.line),
    ...(model.choices ?? []).flatMap((c) => (c.line ? [c.line] : [])),
  ];
  const atoms = texts.flatMap((t) => {
    try {
      return kanaLineAtoms(t);
    } catch {
      return [];
    }
  });
  const names: string[] = [];
  const add = (n: string) => {
    if (!names.includes(n)) names.push(n);
  };
  const startHead = kanaLineAtoms(spec.start)[0];
  if (startHead) add(startHead);
  add(spec.goal.target);
  [...(spec.path ?? []), spec.answer]
    .map((t) => kanaLineAtoms(t))
    .flatMap((a) => a)
    .filter((a) => NAME_RE.test(a))
    .forEach(add);
  const x = NAME_RE.exec(startHead ?? '')?.[3] ?? 'x';
  add(x);
  // moteur v44 : la constante e (« f(e) », « y = x ÷ e »)
  if ((spec.goal.kind === 'tangent' || spec.condition) && atoms.includes('e')) add('e');
  // une primitive : « + C » toujours possible
  if (spec.goal.kind === 'antiderivative') add('C');
  const nums = [...new Set(atoms.filter((a) => /^\d+(,\d+)?$/.test(a)))];
  const val = (s: string) => Number(s.replace(',', '.'));
  nums.sort((a, b) => val(a) - val(b) || a.localeCompare(b));
  const used = new Set(atoms);
  const sups = [...new Set(atoms.filter((a) => isSupRun(a) && a !== '²'))].sort(
    (a, b) => supValue(a) - supValue(b),
  );
  const always = new Set(['+', '−', '×', '÷', '(', ')', '=']);
  const ops: string[] = [];
  for (const o of ANALYSIS_OPS) {
    if (always.has(o) || used.has(o)) ops.push(o);
    if (o === '²') ops.push(...sups);
  }
  return { names, numbers: nums, ops };
}

const supValue = (s: string): number =>
  Number([...s].map((c) => String(SUP_DIGITS.indexOf(c))).join(''));

/** pour les tests */
export const _caretAtoms = caretAtoms;
